/**
 * Per-tool success-state copy for the free /tools/* funnel (W.4.51).
 *
 * History: this module used to feed a post-download popup (SuccessStudioModal)
 * and an inline banner that both pushed the visitor to Studio. PostHog session
 * replay showed the popup landing on top of the result the moment the job
 * finished — it interrupted the very outcome the visitor came for, and its
 * only exit was "go to Studio". Both surfaces are gone. What remains is a
 * plain, tool-specific success state rendered inline under the runner:
 *
 *   1. It confirms what the tool just produced, in past tense.
 *   2. It says where the output went.
 *   3. It offers sibling /tools/* pages as the next step.
 *
 * Rule: nothing here may link to Studio, an account, or any other surface off
 * the tools funnel. `nextTools` slugs must exist in `data.ts`
 * (tool-success-copy.test.ts pins that).
 */

export interface ToolSuccessCopy {
  /** Past-tense headline — the job is finished, not "could be". */
  title: string;
  /** One line: what was produced and where it went. */
  body: string;
  /** Sibling tool slugs offered as the next step. Never off-funnel. */
  nextTools: string[];
}

export type ToolSuccessVariant = 'redact-overlay' | 'compress-original' | 'pdf-ready-on-page';

export const COPY_BY_VARIANT: Record<ToolSuccessVariant, ToolSuccessCopy> = {
  'redact-overlay': {
    title: 'Overlay copy downloaded',
    body: 'The black boxes only cover the content visually. The original text is still recoverable. Use True removal before sharing sensitive information.',
    nextTools: ['redact', 'flatten', 'compress'],
  },
  'compress-original': {
    title: 'Original PDF downloaded',
    body: 'Compression did not make this PDF smaller. Your original file has downloaded unchanged.',
    nextTools: ['split', 'metadata', 'merge'],
  },
  // W.4.116 (#2684): on an iPhone or iPad the PDF does not download by itself;
  // the runner shows Open PDF and Share or save instead.
  'pdf-ready-on-page': {
    title: 'PDF ready',
    body: 'Open it or share it with the buttons above. Add more images above to build another.',
    nextTools: ['merge', 'compress', 'organize'],
  },
};

export const DEFAULT_COPY: ToolSuccessCopy = {
  title: 'Done',
  body: 'Your file is ready. Run another tool below, or drop in a new file to start again.',
  nextTools: ['merge', 'compress', 'split'],
};

function copy(title: string, body: string, nextTools: string[]): ToolSuccessCopy {
  return { title, body, nextTools };
}

export const COPY_BY_SLUG: Record<string, ToolSuccessCopy> = {
  // ── Organize ──────────────────────────────────────────────────────
  organize: copy(
    'Pages reorganized',
    'Your reordered PDF has been saved to your device. Drop in another file to organize the next one.',
    ['merge', 'split', 'rotate'],
  ),
  merge: copy(
    'PDFs merged',
    'Your combined PDF has downloaded. Add more files above to build another one.',
    ['compress', 'organize', 'split'],
  ),
  split: copy(
    'PDF split',
    'Your page ranges have downloaded as separate PDFs. Pick new ranges above to split again.',
    ['merge', 'organize', 'crop'],
  ),

  // ── Optimize / repair ─────────────────────────────────────────────
  compress: copy(
    'PDF compressed',
    'The smaller file has downloaded. Drop in another PDF to compress the next one.',
    ['merge', 'split', 'metadata'],
  ),
  flatten: copy(
    'PDF flattened',
    'Form fields and annotations are now baked into the page content, and the flattened copy has downloaded.',
    ['sign', 'redact', 'compress'],
  ),
  ocr: copy(
    'Text layer added',
    'Your scanned PDF is searchable and selectable, and the OCR copy has downloaded.',
    ['pdf-to-word', 'pdf-to-excel', 'handwriting-ocr'],
  ),
  compare: copy(
    'Comparison ready',
    'The differences between the two versions are listed above. Load another pair to compare again.',
    ['metadata', 'organize', 'highlight'],
  ),
  metadata: copy(
    'Metadata inspected',
    'Everything this PDF reveals — properties, fonts, and security settings — is listed above.',
    ['redact', 'flatten', 'compress'],
  ),

  // ── Accessibility checks ──────────────────────────────────────────
  'verapdf-checker': copy(
    'Preflight complete',
    'Your PDF/UA results are listed above, with each finding pointed at the clause it comes from.',
    ['well-tagged-pdf-checker', 'matterhorn-pdf-checker', 'pdf-to-html'],
  ),
  'matterhorn-pdf-checker': copy(
    'Matterhorn check complete',
    'Failure conditions are grouped above by checkpoint, including the ones that need a human review.',
    ['verapdf-checker', 'well-tagged-pdf-checker', 'pdf-to-html'],
  ),
  'well-tagged-pdf-checker': copy(
    'Tag check complete',
    'The tag tree, reading-order signals, and structure gaps for this PDF are summarized above.',
    ['verapdf-checker', 'matterhorn-pdf-checker', 'pdf-to-html'],
  ),

  // ── Convert to PDF ────────────────────────────────────────────────
  'word-to-pdf': copy(
    'Word file converted',
    'Your PDF has downloaded with the original layout intact. Drop in another document to convert it.',
    ['merge', 'compress', 'watermark'],
  ),
  'excel-to-pdf': copy(
    'Spreadsheet converted',
    'Your PDF has downloaded. Drop in another workbook to convert the next one.',
    ['merge', 'compress', 'page-numbers'],
  ),
  'jpg-to-pdf': copy(
    'Images converted to PDF',
    'Your PDF has downloaded with the pages in the order you set. Add more images above to build another.',
    ['merge', 'compress', 'organize'],
  ),
  'png-to-pdf': copy(
    'Images converted to PDF',
    'Your PDF has downloaded with the pages in the order you set. Add more images above to build another.',
    ['merge', 'compress', 'organize'],
  ),

  // ── Convert from PDF ──────────────────────────────────────────────
  'pdf-to-word': copy(
    'Word document ready',
    'Your editable .docx has downloaded with text, layout, and tables carried over.',
    ['pdf-to-excel', 'ocr', 'pdf-to-json'],
  ),
  'pdf-to-excel': copy(
    'Spreadsheet ready',
    'Your tables have downloaded as rows you can open in Excel or Google Sheets.',
    ['pdf-to-json', 'pdf-to-word', 'ocr'],
  ),
  'pdf-to-markdown': copy(
    'Markdown ready',
    'Your .md file has downloaded with headings, tables, and reading order reconstructed by ParseBench.',
    ['pdf-to-json', 'pdf-to-html', 'ocr'],
  ),
  extract: copy(
    'Tables extracted',
    'Your structured rows are ready to download. Load another PDF to pull the next set.',
    ['pdf-to-excel', 'pdf-to-json', 'ocr'],
  ),
  'pdf-to-json': copy(
    'JSON ready',
    'Your document is now structured JSON with the fields and types you asked for.',
    ['pdf-to-excel', 'pdf-to-word', 'pdf-to-html'],
  ),
  'pdf-to-html': copy(
    'HTML page ready',
    'Your PDF is now an accessible HTML page with headings, tables, and reading order preserved.',
    ['well-tagged-pdf-checker', 'verapdf-checker', 'pdf-to-json'],
  ),
  'handwriting-ocr': copy(
    'Handwriting transcribed',
    'The recognized text is above and ready to copy or export.',
    ['ocr', 'pdf-to-word', 'pdf-to-excel'],
  ),
  'pdf-to-link': copy(
    'Share link ready',
    'Copy the link above to send this PDF. It opens in any browser — no reader install, no account.',
    ['redact', 'compress', 'watermark'],
  ),
  'pdf-to-jpg': copy(
    'Pages exported as JPG',
    'Your images have downloaded at the quality you picked. Load another PDF to export more pages.',
    ['pdf-to-png', 'compress', 'crop'],
  ),
  'pdf-to-png': copy(
    'Pages exported as PNG',
    'Your images have downloaded at full page resolution. Load another PDF to export more pages.',
    ['pdf-to-jpg', 'compress', 'crop'],
  ),

  // ── Edit ──────────────────────────────────────────────────────────
  watermark: copy(
    'Watermark applied',
    'Your marked PDF has downloaded with the watermark on every page you selected.',
    ['page-numbers', 'flatten', 'compress'],
  ),
  'page-numbers': copy(
    'Page numbers added',
    'Your numbered PDF has downloaded with the position and format you chose.',
    ['bates', 'watermark', 'merge'],
  ),
  bates: copy(
    'Bates numbers applied',
    'Your stamped PDF has downloaded with a unique, sequential number on every page.',
    ['page-numbers', 'redact', 'merge'],
  ),
  redact: copy(
    'Redactions burned in',
    'The covered content is removed from the file itself, not just hidden, and the redacted copy has downloaded.',
    ['flatten', 'metadata', 'compress'],
  ),
  highlight: copy(
    'Highlights applied',
    'Your annotated PDF has downloaded with every highlight in place.',
    ['flatten', 'sign', 'merge'],
  ),
  sign: copy('PDF signed', 'Your signed PDF has downloaded and is ready to send or file.', [
    'flatten',
    'merge',
    'compress',
  ]),
  crop: copy(
    'Pages cropped',
    'Your trimmed PDF has downloaded with the new page boundaries applied.',
    ['rotate', 'organize', 'compress'],
  ),
  rotate: copy(
    'Pages rotated',
    'Your corrected PDF has downloaded with every page facing the right way.',
    ['crop', 'organize', 'merge'],
  ),
};

export function resolveToolSuccessCopy(toolSlug: string): ToolSuccessCopy {
  return COPY_BY_SLUG[toolSlug] ?? DEFAULT_COPY;
}
